import React, { useState, useEffect } from 'react';
import { useAuth, apiClient } from '../App';
import Layout from '../components/Layout';
import { Trophy, Medal, Flame, Crown, Loader2, Sparkles, MessageCircle, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { hikersHeroBgStyle } from '../constants/backgrounds';

const BADGE_ICON_MAP = {
  trophy: '🏆', star: '⭐', flame: '🔥', zap: '⚡', crown: '👑',
  medal: '🥇', droplets: '💧', heart: '❤️', target: '🎯',
  sparkles: '✨', sun: '☀️', moon: '🌙', leaf: '🌿', award: '🏅',
  bolt: '⚡', check: '✅',
};

const badgeIconEmoji = (raw) => {
  if (!raw) return '🏆';
  const s = String(raw).trim();
  if (!s) return '🏆';
  // If already an emoji (any non-ASCII char), pass through
  if (/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u.test(s)) return s;
  return BADGE_ICON_MAP[s.toLowerCase()] || '🏆';
};

const Leaderboard = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingFistBump, setSendingFistBump] = useState(null);
  const [givenFistBumps, setGivenFistBumps] = useState(new Set());
  const [leaderboardInfo, setLeaderboardInfo] = useState(null);
  const [recentWins, setRecentWins] = useState([]);
  const [recentWinsLoading, setRecentWinsLoading] = useState(true);
  const [bumpingMilestoneId, setBumpingMilestoneId] = useState(null);
  const [coachMessageTarget, setCoachMessageTarget] = useState(null); // {user_id, user_name}
  const [coachMessageText, setCoachMessageText] = useState('');
  const [sendingCoachMessage, setSendingCoachMessage] = useState(false);
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchLeaderboard();
    fetchGivenFistBumps();
    fetchRecentWins();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const [boardRes, rankRes, infoRes] = await Promise.all([
        apiClient.get('/leaderboard'),
        apiClient.get('/leaderboard/me'),
        apiClient.get('/leaderboard/info'),
      ]);
      setLeaderboard(boardRes.data);
      setMyRank(rankRes.data);
      setLeaderboardInfo(infoRes.data);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGivenFistBumps = async () => {
    try {
      const res = await apiClient.get('/reactions/given');
      // Create a set of "userId_date" to track who we've fist bumped today
      const given = new Set(
        res.data
          .filter(r => r.date === today)
          .map(r => `${r.to_user_id}_${r.date}`)
      );
      setGivenFistBumps(given);
    } catch (err) {
      console.error('Failed to fetch given fist bumps:', err);
    }
  };

  const fetchRecentWins = async () => {
    setRecentWinsLoading(true);
    try {
      const res = await apiClient.get('/group-milestones/recent?days=7');
      setRecentWins(res.data.achievements || []);
    } catch (err) {
      console.error('Failed to fetch recent wins:', err);
    } finally {
      setRecentWinsLoading(false);
    }
  };

  const formatRelativeDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString();
  };

  const bumpMilestone = async (achievementId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setBumpingMilestoneId(achievementId);
    try {
      const res = await apiClient.post(`/group-milestones/${achievementId}/fistbump`);
      const bumpedNow = !!res.data.fistbumped;
      toast.success(res.data.message);
      setRecentWins((prev) =>
        prev.map((w) =>
          w.id === achievementId
            ? {
                ...w,
                user_fistbumped: bumpedNow,
                fistbump_count: Math.max(0, (w.fistbump_count || 0) + (bumpedNow ? 1 : -1)),
              }
            : w
        )
      );
    } catch (err) {
      toast.error('Failed to send fist bump');
    } finally {
      setBumpingMilestoneId(null);
    }
  };

  const sendFistBump = async (toUserId, userName) => {
    if (toUserId === user?.id) {
      toast.error("You can't fist bump yourself!");
      return;
    }

    setSendingFistBump(toUserId);
    try {
      await apiClient.post('/reactions', {
        to_user_id: toUserId,
        date: today,
        reaction_type: 'fistbump'
      });
      toast.success(`👊 Fist bump sent to ${userName}!`);
      setGivenFistBumps(prev => new Set([...prev, `${toUserId}_${today}`]));
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send fist bump';
      toast.error(message);
    } finally {
      setSendingFistBump(null);
    }
  };

  const openCoachMessage = (entry) => {
    setCoachMessageTarget({ user_id: entry.user_id, user_name: entry.user_name });
    setCoachMessageText('');
  };

  const sendCoachMessage = async () => {
    const text = coachMessageText.trim();
    if (!text) {
      toast.error('Add a message before sending.');
      return;
    }
    setSendingCoachMessage(true);
    try {
      const res = await apiClient.post(`/coach/${coachMessageTarget.user_id}/message`, { message: text });
      const remaining = res.data?.remaining_today;
      toast.success(
        typeof remaining === 'number'
          ? `Message sent! ${remaining} more allowed today.`
          : 'Message sent!'
      );
      setCoachMessageTarget(null);
      setCoachMessageText('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send message');
    } finally {
      setSendingCoachMessage(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="text-yellow-400" size={24} />;
    if (rank === 2) return <Medal className="text-gray-400" size={24} />;
    if (rank === 3) return <Medal className="text-amber-600" size={24} />;
    return <span className="text-stone-500 font-bold">#{rank}</span>;
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/20 to-transparent border-yellow-500/50';
    if (rank === 2) return 'bg-gradient-to-r from-gray-400/20 to-transparent border-gray-400/50';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-transparent border-amber-600/50';
    return 'bg-stone-100/30 border-stone-300/50';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={hikersHeroBgStyle} className="min-h-screen" data-testid="leaderboard-page">
        <div className="p-4 md:p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#EFB342]/20 rounded-2xl mb-4">
            <Trophy className="text-[#EFB342]" size={32} />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-2">LEADERBOARD</h1>
          <p className="text-stone-600 text-base">
            {leaderboardInfo?.message || 'Monthly Challenge'} • Days 1-28
          </p>
          {leaderboardInfo?.is_previous_month && (
            <p className="text-[#EFB342] text-sm mt-1 font-medium">🏆 Final Results</p>
          )}
          <p className="text-stone-500 text-sm mt-1">Points reset each month</p>
        </div>

        {/* My Rank Card - Sticky at bottom on mobile */}
        {myRank && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-stone-50/95 backdrop-blur-sm border-t border-[#EFB342] z-40">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-[#EFB342] font-bold text-xl">
                  {myRank.is_coach ? '★' : `#${myRank.rank}`}
                </span>
                <div>
                  <p className="text-stone-900 font-medium text-base">{myRank.user_name}</p>
                  <p className="text-sm text-stone-500">
                    {myRank.is_coach ? 'Coach (not ranked)' : 'Your Position'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-[#EFB342]">{myRank.total_points}</p>
                <p className="text-sm text-stone-500">points</p>
              </div>
            </div>
          </div>
        )}

        {/* Top 5 Podium — coaches are shown inline in the list below but not on the podium */}
        {(() => {
          const podium = leaderboard.filter((e) => !e.is_coach);
          return (
        <>
        <div className="flex items-end justify-center gap-2 md:gap-4 mb-4">
          {/* 2nd Place */}
          {podium[1] && (
            <div className="flex flex-col items-center animate-fade-in stagger-2">
              <div className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-stone-300 border-2 border-stone-400 flex items-center justify-center mb-2 md:mb-3 shadow-md">
                <Medal className="text-stone-600" size={26} strokeWidth={2.5} />
              </div>
              <p className="text-stone-900 font-bold truncate max-w-[80px] md:max-w-[120px] text-sm md:text-lg drop-shadow-sm">{podium[1].user_name}</p>
              <p className="text-stone-800 text-sm md:text-base font-semibold">{podium[1].total_points} pts</p>
              <div className="w-16 h-16 md:w-24 md:h-24 bg-stone-300 border-2 border-stone-400 border-b-0 rounded-t-xl mt-2 flex items-center justify-center shadow-md">
                <span className="text-2xl md:text-3xl font-black text-stone-700 drop-shadow-sm">2</span>
              </div>
            </div>
          )}
          
          {/* 1st Place */}
          {podium[0] && (
            <div className="flex flex-col items-center animate-fade-in stagger-1">
              <div className="w-18 h-18 md:w-24 md:h-24 rounded-full bg-[#EFB342] border-2 border-[#B8871E] flex items-center justify-center mb-2 md:mb-3 shadow-lg" style={{width: '72px', height: '72px'}}>
                <Crown className="text-black" size={34} strokeWidth={2.5} />
              </div>
              <p className="text-stone-900 font-bold truncate max-w-[100px] md:max-w-[140px] text-sm md:text-lg drop-shadow-sm">{podium[0].user_name}</p>
              <p className="text-stone-800 text-sm md:text-base font-semibold">{podium[0].total_points} pts</p>
              <div className="w-20 h-24 md:w-28 md:h-32 bg-[#EFB342] border-2 border-[#B8871E] border-b-0 rounded-t-xl mt-2 flex items-center justify-center shadow-lg">
                <span className="text-3xl md:text-4xl font-black text-black drop-shadow">1</span>
              </div>
            </div>
          )}
          
          {/* 3rd Place */}
          {podium[2] && (
            <div className="flex flex-col items-center animate-fade-in stagger-3">
              <div className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-[#C99B5B] border-2 border-[#A67A3F] flex items-center justify-center mb-2 md:mb-3 shadow-md">
                <Medal className="text-white" size={26} strokeWidth={2.5} />
              </div>
              <p className="text-stone-900 font-bold truncate max-w-[80px] md:max-w-[120px] text-sm md:text-lg drop-shadow-sm">{podium[2].user_name}</p>
              <p className="text-stone-800 text-sm md:text-base font-semibold">{podium[2].total_points} pts</p>
              <div className="w-16 h-14 md:w-24 md:h-20 bg-[#C99B5B] border-2 border-[#A67A3F] border-b-0 rounded-t-xl mt-2 flex items-center justify-center shadow-md">
                <span className="text-2xl md:text-3xl font-black text-white drop-shadow-sm">3</span>
              </div>
            </div>
          )}
        </div>

        {/* 4th and 5th place strip — sits under the podium so the top 5 stand out */}
        {(podium[3] || podium[4]) && (
          <div className="flex items-stretch justify-center gap-2 md:gap-3 mb-8 max-w-md mx-auto">
            {podium[3] && (
              <div className="flex-1 bg-white border border-stone-300 rounded-xl px-3 py-2 flex items-center gap-2 shadow-sm" data-testid="podium-4th">
                <span className="text-lg font-black text-stone-800 shrink-0">4</span>
                <div className="min-w-0 flex-1">
                  <p className="text-stone-900 text-sm font-semibold truncate">{podium[3].user_name}</p>
                  <p className="text-stone-700 text-xs font-medium">{podium[3].total_points} pts</p>
                </div>
              </div>
            )}
            {podium[4] && (
              <div className="flex-1 bg-white border border-stone-300 rounded-xl px-3 py-2 flex items-center gap-2 shadow-sm" data-testid="podium-5th">
                <span className="text-lg font-black text-stone-800 shrink-0">5</span>
                <div className="min-w-0 flex-1">
                  <p className="text-stone-900 text-sm font-semibold truncate">{podium[4].user_name}</p>
                  <p className="text-stone-700 text-xs font-medium">{podium[4].total_points} pts</p>
                </div>
              </div>
            )}
          </div>
        )}
        </>
          );
        })()}

        {/* Full Leaderboard List */}
        <div className="bg-white/50 border border-stone-200 rounded-2xl overflow-hidden mb-20 lg:mb-0">
          <div className="p-4 border-b border-stone-200">
            <h2 className="font-heading text-lg font-bold text-stone-900">ALL PARTICIPANTS</h2>
            <p className="text-sm text-stone-500 mt-1">👊 Send a fist bump to encourage others!</p>
          </div>
          
          <div className="divide-y divide-zinc-800">
            {leaderboard.map((entry, index) => {
              const isMe = entry.user_id === user?.id;
              const hasFistBumped = givenFistBumps.has(`${entry.user_id}_${today}`);
              const isCoach = !!entry.is_coach;
              
              return (
                <div 
                  key={entry.user_id}
                  className={`flex items-center gap-4 p-4 transition-colors ${
                    isMe ? 'bg-[#EFB342]/10' : isCoach ? 'bg-[#EFB342]/5' : 'hover:bg-stone-100/30'
                  }`}
                  data-testid={`leaderboard-entry-${index}`}
                >
                  {/* Rank / Coach badge */}
                  <div className="w-12 flex items-center justify-center">
                    {isCoach ? (
                      <span className="text-[#EFB342] text-xl" title="Coach">★</span>
                    ) : (
                      getRankIcon(entry.rank)
                    )}
                  </div>
                  
                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate text-base ${isMe ? 'text-[#EFB342]' : isCoach ? 'text-[#EFB342]' : 'text-stone-900'}`}>
                      {entry.user_name}
                      {isCoach && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#EFB342]/20 text-[#EFB342] px-1.5 py-0.5 rounded-full font-bold">Coach</span>}
                      {isMe && <span className="ml-2 text-sm text-[#EFB342]/70">(You)</span>}
                    </p>
                    {isCoach ? (
                      <p className="text-xs text-stone-500 italic">Excluded from leaderboard totals</p>
                    ) : (
                      <div className="flex items-center gap-4 text-sm text-stone-500">
                        <span className="flex items-center gap-1">
                          <Flame size={14} className="text-orange-500" />
                          {entry.current_streak} day streak
                        </span>
                        <span>{entry.badges_count} badges</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Fist Bump Button (for both regular members and coaches) */}
                  {!isMe && (
                    <button
                      onClick={() => sendFistBump(entry.user_id, entry.user_name)}
                      disabled={sendingFistBump === entry.user_id || hasFistBumped}
                      className={`p-2 rounded-full transition-all text-xl ${
                        hasFistBumped 
                          ? 'bg-[#EFB342]/20 cursor-default' 
                          : 'bg-stone-100 hover:bg-[#EFB342]/20 grayscale hover:grayscale-0'
                      }`}
                      title={hasFistBumped ? 'Fist bump sent!' : `Send fist bump to ${entry.user_name}`}
                      data-testid={`fistbump-${entry.user_id}`}
                    >
                      {sendingFistBump === entry.user_id ? (
                        <div className="w-5 h-5 border-2 border-[#EFB342]/30 border-t-[#EFB342] rounded-full animate-spin" />
                      ) : (
                        <span role="img" aria-label="fist bump">👊</span>
                      )}
                    </button>
                  )}
                  {/* Message Coach button (only for coaches) */}
                  {!isMe && isCoach && (
                    <button
                      onClick={() => openCoachMessage(entry)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EFB342]/15 border border-[#EFB342]/40 text-[#EFB342] hover:bg-[#EFB342]/25 text-xs font-semibold transition-colors"
                      title={`Send ${entry.user_name} a direct message`}
                      data-testid={`coach-message-btn-${entry.user_id}`}
                    >
                      <MessageCircle size={13} />
                      Message
                    </button>
                  )}
                  
                  {/* Points */}
                  <div className="text-right">
                    <p className={`text-xl font-bold ${isMe ? 'text-[#EFB342]' : isCoach ? 'text-[#EFB342]' : 'text-stone-900'}`}>
                      {entry.total_points}
                    </p>
                    <p className="text-sm text-stone-500">points</p>
                  </div>
                </div>
              );
            })}
            
            {leaderboard.length === 0 && (
              <div className="p-8 text-center text-stone-500">
                No participants yet. Be the first to join!
              </div>
            )}
          </div>
        </div>

        {/* Desktop My Rank Card */}
        {myRank && (
          <div className="hidden lg:block mt-6 bg-white border border-[#EFB342] rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#EFB342]/20 rounded-xl flex items-center justify-center">
                  <span className="text-[#EFB342] font-bold text-xl">
                    {myRank.is_coach ? '★' : `#${myRank.rank}`}
                  </span>
                </div>
                <div>
                  <p className="text-stone-900 font-semibold text-lg">{myRank.user_name}</p>
                  <p className="text-base text-stone-500">
                    {myRank.is_coach ? 'Coach · not ranked' : 'Your Current Position'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#EFB342]">{myRank.total_points}</p>
                  <p className="text-sm text-stone-500 uppercase">Points</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-orange-500">{myRank.current_streak}</p>
                  <p className="text-sm text-stone-500 uppercase">Day Streak</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-500">{myRank.badges_count}</p>
                  <p className="text-sm text-stone-500 uppercase">Badges</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* This Week's Wins Section */}
        <div className="mt-6 bg-white/50 border border-stone-200 rounded-2xl overflow-hidden" data-testid="this-weeks-wins">
          <div className="p-4 border-b border-stone-200 flex items-center gap-2">
            <Sparkles className="text-[#EFB342]" size={20} />
            <h2 className="font-heading text-lg font-bold text-stone-900">MEMBER WINS THIS WEEK!</h2>
            <span className="text-xs text-stone-500 ml-auto">last 7 days</span>
          </div>
          
          <div className="p-4">
            {recentWinsLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="animate-spin text-[#EFB342]" size={24} />
              </div>
            ) : recentWins.length === 0 ? (
              <div className="text-center py-6">
                <Sparkles className="mx-auto text-stone-400 mb-3" size={36} />
                <p className="text-stone-500 text-base">No wins in the past week yet — be the first to log one!</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {recentWins.map((win) => {
                  const isMine = win.user_id === user?.id;
                  return (
                    <div
                      key={win.id}
                      className="flex items-center gap-3 bg-white/60 border border-stone-200 rounded-xl p-3 hover:border-[#EFB342]/40 transition-colors"
                      data-testid={`weekly-win-${win.id}`}
                    >
                      <div className="w-10 h-10 shrink-0 bg-[#EFB342]/10 rounded-lg flex items-center justify-center text-xl">
                        {badgeIconEmoji(win.badge_icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-stone-900 text-sm font-semibold truncate">
                          {isMine ? 'You' : win.user_name}
                        </p>
                        <p className="text-stone-600 text-xs truncate">
                          {win.badge_name} • {formatRelativeDate(win.earned_at)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => bumpMilestone(win.id, e)}
                        disabled={bumpingMilestoneId === win.id || isMine}
                        title={isMine ? "You can't fist-bump your own milestone" : (win.user_fistbumped ? 'Remove your fist bump' : 'Send a fist bump')}
                        data-testid={`weekly-win-fistbump-${win.id}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all shrink-0 ${
                          isMine
                            ? 'bg-white border-stone-200 text-stone-400 cursor-not-allowed'
                            : win.user_fistbumped
                            ? 'bg-[#EFB342]/20 border-[#EFB342]/60 text-[#EFB342] cursor-pointer'
                            : 'bg-stone-100/60 border-stone-300 text-stone-900 hover:bg-stone-100 hover:border-[#EFB342]/40 cursor-pointer'
                        } ${bumpingMilestoneId === win.id ? 'opacity-70 cursor-wait' : ''}`}
                      >
                        {bumpingMilestoneId === win.id ? (
                          <Loader2 className="animate-spin" size={14} />
                        ) : (
                          <>👊 {win.fistbump_count || 0}</>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Coach Message Modal */}
      {coachMessageTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/50 backdrop-blur-sm"
          onClick={() => !sendingCoachMessage && setCoachMessageTarget(null)}
          data-testid="coach-message-modal"
        >
          <div
            className="w-full max-w-md bg-white border border-[#EFB342]/40 rounded-2xl p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-[#EFB342]/20 border border-[#EFB342]/40 flex items-center justify-center">
                  <MessageCircle className="text-[#EFB342]" size={18} />
                </div>
                <div>
                  <p className="text-stone-900 font-semibold text-base leading-tight">Message {coachMessageTarget.user_name}</p>
                  <p className="text-stone-500 text-xs">Delivered as an in-app notification + email</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !sendingCoachMessage && setCoachMessageTarget(null)}
                className="text-stone-600 hover:text-stone-900 p-1"
                data-testid="coach-message-close"
              >
                <X size={18} />
              </button>
            </div>
            <textarea
              value={coachMessageText}
              onChange={(e) => setCoachMessageText(e.target.value.slice(0, 500))}
              rows={4}
              placeholder="e.g. Coach, I'm struggling with the sleep habit — any tips?"
              className="w-full bg-white border border-stone-300 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-500 focus:outline-none focus:border-[#EFB342]/60 resize-none"
              data-testid="coach-message-textarea"
              autoFocus
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-stone-500">{coachMessageText.length}/500 · limit 3 per day</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCoachMessageTarget(null)}
                  disabled={sendingCoachMessage}
                  className="text-stone-600 hover:text-stone-900 text-sm px-3 py-1.5 rounded-lg"
                  data-testid="coach-message-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={sendCoachMessage}
                  disabled={sendingCoachMessage || !coachMessageText.trim()}
                  className="bg-[#EFB342] text-black hover:bg-[#D99F30] disabled:opacity-60 text-sm font-semibold px-4 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
                  data-testid="coach-message-send"
                >
                  {sendingCoachMessage ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default Leaderboard;
